import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubmitHandler, useForm } from 'react-hook-form';
import { forEach } from 'lodash';
import { useHookFormMask } from 'use-mask-input';
import { toast } from '@/components/ui/Toast';
import { useFormStore } from '@/stores/formStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ContactForm, DEFAULT_VALUES, contactSchema } from './schema';

export function useContactForm({ isPage }: { isPage?: boolean } = {}) {
  const setIsFormSubmittedSuccess = useFormStore((store) => store.setIsFormSubmittedSuccess);
  const setIsFormSubmittedFailure = useFormStore((store) => store.setIsFormSubmittedFailure);
  const nextStep = useFormStore((store) => store.nextStep);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const form = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    defaultValues: DEFAULT_VALUES
  });

  const registerWithMask = useHookFormMask(form?.register);

  const handleContactSubmit: SubmitHandler<ContactForm> = async (data) => {
    if (!executeRecaptcha) {
      toast.error('reCAPTCHA não disponível');
      return;
    }

    setIsFormSubmittedFailure(false);

    try {
      const gReCaptchaToken = await executeRecaptcha('helpCenterFormSubmit');

      const formData = new FormData();
      formData.set('subjectCategory', data.subjectCategory);
      formData.set('subject', data.subject);
      formData.set('name', data.name);
      formData.set('socialName', data.socialName || '');
      formData.set('linkWithCompany', data.linkWithCompany);
      formData.set('email', data.email);
      formData.set('identity', data.identity || '');
      formData.set('phone', data.phone || '');
      formData.set('message', data.message);
      if (data.file) {
        forEach(data.file, (file) => formData.append('file', file));
      }
      formData.set('gReCaptchaToken', gReCaptchaToken);

      const response = await fetch('/api/send-mail/help-center', {
        method: 'POST',
        body: formData
      });

      const json = await response.json();

      if (json?.data?.success) {
        form?.reset(DEFAULT_VALUES);
        const inputFile = document.querySelector<HTMLInputElement>('#file');
        if (inputFile) inputFile.value = '';

        if (json?.data?.message) {
          if (isPage) {
            setIsFormSubmittedSuccess(true);
            nextStep();
            return;
          }

          toast.info(json.data.message);
        }
      } else {
        if (json?.error?.message) {
          if (isPage) {
            setIsFormSubmittedFailure(true);
            nextStep();
          }

          toast.error(json.error.message);
        }
      }
    } catch (err: any) {
      if (isPage) {
        setIsFormSubmittedFailure(true);
        nextStep();
      }

      toast.error(err?.error?.message || 'Algo deu errado, tente novamente mais tarde');
    }
  };

  return {
    form,
    registerWithMask,
    handleContactSubmit
  };
}
